import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../utils/axios';
import socket from '../../utils/socket';

import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import HomeIcon from '@mui/icons-material/Home';
import StarIcon from '@mui/icons-material/Star';
import ChatIcon from '@mui/icons-material/Chat';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import { useAuth } from './../services/AuthContext';
import { useTranslation } from 'react-i18next';

const BottomBar = () => {
  const { t } = useTranslation();
  const { getUserUUID, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [value, setValue] = useState('/');
  const [markedProductsCount, setMarkedProductsCount] = useState(0);
  const [userId, setUserId] = useState(null);
  const socketConnected = useRef(false);

  // Fetch and store user UUID
  useEffect(() => {
    if (isAuthenticated()) {
      try {
        const id = getUserUUID();
        if (id) {
          setUserId(id);
          console.log('User UUID:', id);
        } else {
          console.warn('No user UUID found');
        }
      } catch (error) {
        console.error('Error retrieving user UUID:', error);
      }
    } else {
      console.warn('User is not authenticated');
    }
  }, [isAuthenticated]);

  // Setup WebSocket connection
  useEffect(() => {
    if (!userId || socketConnected.current) return;

    socket.connect();
    socketConnected.current = true;

    socket.once('connect', () => {
      console.log('WebSocket connected');
      socket.emit('join', { userId }); // Optional, if backend expects join
    });

    socket.on('reconnect', () => {
      console.log('WebSocket reconnected');
      socket.emit('join', { userId });
    });

    socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error.message);
    });

    socket.on('product_marked', () =>
      setMarkedProductsCount((prev) => prev + 1)
    );

    socket.on('product_unmarked', () =>
      setMarkedProductsCount((prev) => Math.max(prev - 1, 0))
    );

    return () => {
      socket.off('connect');
      socket.off('connect_error');
      socket.off('reconnect');
      socket.off('product_marked');
      socket.off('product_unmarked');
      socket.disconnect();
      socketConnected.current = false;
    };
  }, [userId]);

  // Fetch initial count of marked products
  useEffect(() => {
    if (!userId) return;

    const fetchMarkedCount = async () => {
      try {
        const response = await api.get('/api/marked-products', {
          params: { userUid: userId },
        });
        if (typeof response.data?.count === 'number') {
          setMarkedProductsCount(response.data.count);
        }
      } catch (error) {
        console.error('Failed to fetch marked products count:', error.message);
      }
    };

    fetchMarkedCount();
    setValue(location.pathname);
  }, [location.pathname, userId]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
    navigate(newValue);
  };

  return (
    <Box sx={{ width: '100%', position: 'fixed', bottom: 0 }}>
      <BottomNavigation value={value} onChange={handleChange} showLabels>
        <BottomNavigationAction
          label={t('bottom_menu.home')}
          value="/"
          icon={
            <HomeIcon
              sx={{ fontSize: 28, color: value === '/' ? '#FF5864' : '#808080' }}
            />
          }
        />
        <BottomNavigationAction
          label={t('bottom_menu.marked')}
          value="/marked"
          icon={
            <Badge badgeContent={markedProductsCount} color="error" overlap="circular">
              <StarIcon
                sx={{
                  fontSize: 28,
                  color: value === '/marked' ? '#FF5864' : '#808080',
                }}
              />
            </Badge>
          }
        />
        <BottomNavigationAction
          label={t('bottom_menu.messages')}
          value="/messages"
          icon={
            <ChatIcon
              sx={{
                fontSize: 28,
                color: value === '/messages' ? '#FF5864' : '#808080',
              }}
            />
          }
        />
        <BottomNavigationAction
          label={t('bottom_menu.profile')}
          value="/profile"
          icon={
            <AccountCircleIcon
              sx={{
                fontSize: 28,
                color: value.startsWith('/profile') ? '#FF5864' : '#808080',
              }}
            />
          }
        />
      </BottomNavigation>
    </Box>
  );
};

export default BottomBar;
